import React from 'react';

import {
  useConfirmation,
  Dialog as ConfirmationDialog,
  Response as ConfirmationResponse,
} from '@admin/components/confirmation';
import { client } from '@admin/libraries/apollo';
import { useLeadDeleteMutation, Status } from '@admin/schema';

interface Lead {
  id?: number;
  name?: string;
}

interface DeleteLeadButtonProps {
  lead: Lead;
}

export const DeleteLeadButton: React.FC<DeleteLeadButtonProps> = ({ lead }) => {
  const { confirmation, confirm } = useConfirmation();
  const [error, setError] = React.useState<string | null>(null);

  const [deleteLead, { loading: isDeleting }] = useLeadDeleteMutation({
    client,
    onCompleted: (data) => {
      if (data.leadDelete.status === Status.Ok) {
        window.location.href = '/leads';
      } else {
        const errorMessage = data.leadDelete.error || 'Failed to delete lead. Please try again.';
        setError(errorMessage);
      }
    },
    onError: (error) => {
      console.error('Error deleting lead:', error);
      const errorMessage = error.message || 'Failed to delete lead. Please try again.';
      setError(errorMessage);
    },
  });

  const handleDelete = async () => {
    if (!lead.id || isDeleting) return;

    const confirmed = await confirm({
      title: 'Are you sure you want to delete this lead?',
      description: 'This action cannot be undone.',
    });

    if (confirmed !== ConfirmationResponse.Confirm) return;

    setError(null);

    try {
      await deleteLead({
        variables: { id: lead.id.toString() },
      });
    } catch (error) {
      // Error handled by onError callback
    }
  };

  if (!lead.id) {
    return null;
  }

  return (
    <div>
      {confirmation && <ConfirmationDialog confirmation={confirmation} />}

      <button className="btn btn-danger" onClick={handleDelete} type="button" disabled={isDeleting}>
        {isDeleting ? 'Deleting...' : 'Delete'}
      </button>

      {error && (
        <div className="text-danger" style={{ fontSize: '12px', marginTop: '4px' }}>
          {error}
        </div>
      )}
    </div>
  );
};
